"use client"

import { Empty, Input, Popover } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCheck,
  faChevronDown,
  faGlobe,
  faLocationDot,
  faMagnifyingGlass,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import { useMemo, useState, type FC } from "react"
import { useTranslation } from "react-i18next"
import CountryFlag from "@/components/common/CountryFlag"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { getVisaCountryRegions, type VisaCountryEntry } from "@/utils/country/getVisaCountries"
import { removeVietnameseChars } from "@/utils/string/removeVietnameseChars"
import { VISA_REGION_ICON_MAP } from "./visaRegionIcons"

export type VisaDestinationValue = {
  /** Text hiển thị trong ô input — có thể là tên quốc gia trong danh sách hoặc do khách tự gõ. */
  text: string
  /** Chỉ có giá trị khi text khớp đúng một quốc gia trong danh sách gợi ý. */
  code?: string
}

type SelectDestinationProps = {
  value: VisaDestinationValue | null
  onChange: (value: VisaDestinationValue | null) => void
  className?: string
  onPressEnter?: () => void
  /** Cho phép mở dropdown từ bên ngoài (vd bấm "Xem tất cả" ở danh sách quốc gia phổ biến). */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Bản thu gọn (gắn trong tab-bar trang chủ) — chỉ 1 dòng text + chevron, không có
   * nhãn/gợi ý 2 dòng như bản đầy đủ (field-shell) để khớp layout thanh chung với các tab khác. */
  compact?: boolean
}

/**
 * Dropdown 2 cột (châu lục bên trái, quốc gia bên phải) cấu hình hoàn toàn qua
 * public/static/data/visa-countries.json (xem getVisaCountries.ts) — gõ từ khoá thì chuyển
 * sang danh sách phẳng tìm theo tên (có/không dấu) trên toàn bộ quốc gia. Tên quốc gia/châu lục
 * hiển thị theo đúng ngôn ngữ đang chọn (vi/en) nhờ mỗi entry trong json có sẵn cả 2 tên.
 */
const SelectDestination: FC<SelectDestinationProps> = ({
  value,
  onChange,
  className,
  onPressEnter,
  open: openProp,
  onOpenChange,
  compact = false,
}) => {
  const { t } = useTranslation()
  const { getTranslatedText } = useTETranslation()
  const regions = useMemo(() => getVisaCountryRegions(), [])
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const [activeRegionId, setActiveRegionId] = useState(regions[0]?.id)
  const [searchText, setSearchText] = useState("")

  const isOpen = openProp ?? uncontrolledOpen
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next)
    onOpenChange?.(next)
    if (!next) setSearchText("")
  }

  const countryLabel = (country: VisaCountryEntry) => getTranslatedText(country.name, country.nameEn)
  const activeRegion = regions.find((region) => region.id === activeRegionId) ?? regions[0]
  const normalizedSearch = removeVietnameseChars(searchText.trim()).toLowerCase()

  const searchResults = useMemo(() => {
    if (!normalizedSearch) return []
    const seen = new Set<string>()
    const results: VisaCountryEntry[] = []
    regions.forEach((region) => {
      region.countries.forEach((country) => {
        if (seen.has(country.code)) return
        const haystack = removeVietnameseChars(`${country.name} ${country.nameEn}`).toLowerCase()
        if (haystack.includes(normalizedSearch)) {
          seen.add(country.code)
          results.push(country)
        }
      })
    })
    return results
  }, [normalizedSearch, regions])

  const handleSelect = (country: VisaCountryEntry) => {
    onChange({ text: countryLabel(country), code: country.code })
    setOpen(false)
  }

  const handleClear = () => {
    onChange(null)
    setSearchText("")
  }

  const renderCountryItem = (country: VisaCountryEntry) => (
    <button
      type="button"
      key={country.code}
      className={`visa-destination-popover__item${value?.code === country.code ? " is-selected" : ""}`}
      onClick={() => handleSelect(country)}
    >
      <CountryFlag code={country.code.toLowerCase()} size={20} />
      <span>{countryLabel(country)}</span>
      {value?.code === country.code && <FontAwesomeIcon icon={faCheck} className="visa-destination-popover__check" />}
    </button>
  )

  const content = (
    <div className="visa-destination-popover">
      <div className="visa-destination-popover__search">
        <FontAwesomeIcon icon={faMagnifyingGlass} />
        <input
          autoFocus
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={t("visa.select_destination.search_placeholder")}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return
            if (searchResults.length > 0) {
              handleSelect(searchResults[0])
            } else if (searchText.trim()) {
              onChange({ text: searchText.trim() })
              setOpen(false)
            }
            onPressEnter?.()
          }}
        />
      </div>

      {normalizedSearch ? (
        <div className="visa-destination-popover__list visa-destination-popover__list--full">
          {searchResults.length === 0 ? (
            <Empty description={t("visa.select_destination.no_country_found")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            searchResults.map(renderCountryItem)
          )}
        </div>
      ) : (
        <div className="visa-destination-popover__body">
          <div className="visa-destination-popover__regions">
            {regions.map((region) => (
              <button
                type="button"
                key={region.id}
                className={`visa-destination-popover__region${
                  region.id === activeRegion?.id ? " visa-destination-popover__region--active" : ""
                }`}
                onClick={() => setActiveRegionId(region.id)}
              >
                <FontAwesomeIcon icon={VISA_REGION_ICON_MAP[region.icon] ?? faGlobe} />
                <span>{getTranslatedText(region.name, region.nameEn)}</span>
              </button>
            ))}
          </div>

          <div className="visa-destination-popover__list">{activeRegion?.countries.map(renderCountryItem)}</div>
        </div>
      )}
    </div>
  )

  return (
    <Popover
      open={isOpen}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={content}
      overlayClassName="visa-destination-popover-overlay"
    >
      {compact ? (
        <Input
          size="large"
          readOnly
          value={value?.text}
          placeholder={t("visa.select_destination.placeholder")}
          className={className}
          suffix={
            value?.text ? (
              <FontAwesomeIcon
                icon={faXmark}
                className="visa-destination-clear"
                onClick={(e) => {
                  e.stopPropagation()
                  handleClear()
                }}
              />
            ) : (
              <FontAwesomeIcon icon={faChevronDown} />
            )
          }
        />
      ) : (
        // Field-shell đúng theo ảnh mockup: icon trái (cờ quốc gia khi đã chọn, ngược lại icon
        // pin mặc định) + nhãn câu hỏi cố định phía trên + giá trị/gợi ý phía dưới + chevron phải.
        <div className={`visa__search-field${className ? ` ${className}` : ""}`}>
          <span className="visa__search-field-icon">
            {value?.code ? <CountryFlag code={value.code.toLowerCase()} size={20} /> : <FontAwesomeIcon icon={faLocationDot} />}
          </span>
          <div className="visa__search-field-body">
            <span className="visa__search-field-label">{t("visa.select_destination.placeholder")}</span>
            <span className={`visa__search-field-value${value?.text ? "" : " visa__search-field-value--placeholder"}`}>
              {value?.text || t("visa.select_destination.example_hint")}
            </span>
          </div>
          {value?.text ? (
            <FontAwesomeIcon
              icon={faXmark}
              className="visa__search-field-clear"
              onClick={(e) => {
                e.stopPropagation()
                handleClear()
              }}
            />
          ) : (
            <FontAwesomeIcon icon={faChevronDown} className="visa__search-field-chevron" />
          )}
        </div>
      )}
    </Popover>
  )
}

export default SelectDestination
